import { EXTERNAL_LINKS, useAnalytics } from '@psyomics/ui-common';
import { Button, Puck } from '@psyomics/ui-components';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { ContentType, ReferralInvite } from '../../../../features/referral/generated/referral';
import { LinkExpired } from '../../../routes/referral/views/link-expired';

const getLandingPageConfiguration = (invite: ReferralInvite) => {
  const orgContent = invite.referralSession.referral.organisation?.organisationContent;

  if (!orgContent || orgContent.length === 0) throw new Error('no org content for invite: ' + invite.id);
  const landingPageConfiguration = invite.referralSession.referral.organisation?.organisationContent.find(
    (content) => content.contentType === ContentType.LandingPage
  )?.content.definition;

  if (!landingPageConfiguration) {
    throw new Error('landing page not defined for invite: ' + invite.id);
  }

  return JSON.parse(landingPageConfiguration);
};

const getNextRoute = (invite: ReferralInvite) => (invite.authSystemId ? '/login?redirect=/' : '/register?redirect=/');

const Welcome = ({ invite }: { readonly invite: ReferralInvite }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const analytics = useAnalytics();

  const inviteeName = invite.givenName;
  const organisationName = invite.referralSession.referral.organisation?.name;
  const organisationCode = invite.referralSession.referral.organisation?.code;

  useEffect(() => {
    if (organisationCode) {
      analytics.setGroup({
        groupType: 'organisation',
        groupKey: organisationCode,
      });
    }
  }, [organisationCode, analytics]);

  if (invite.referralSession.status === 'expired') return <LinkExpired />;

  if (!inviteeName || !organisationName) return <Navigate to="/" />;
  const next = getNextRoute(invite);

  const landingPageConfiguration = getLandingPageConfiguration(invite);
  return (
    <div data-testid="referral-welcome">
      <div className="max-w-[680px] w-full mx-auto h-full pt-0 p-6">
        <Puck.LandingPage.Render
          metadata={{
            givenName: inviteeName,
            orgName: organisationName,
            orgNameAbbreviation: invite.referralSession.referral.organisation?.shortName ?? undefined,
            orgPrivacyPolicyUrl: invite.referralSession.referral.organisation?.privacyPolicyUrl ?? undefined,
            psyomicsPrivacyPolicyUrl: EXTERNAL_LINKS.PRIVACY_POLICY,
          }}
          reportDefinition={landingPageConfiguration}
        ></Puck.LandingPage.Render>
        <div className="mt-5.5">
          <Button className="w-full" onClick={() => navigate(next)}>
            {t('page.viewReferral.nextButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export { getNextRoute, Welcome };
